import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Card, Button, Input, Select, Badge, useBestaetigung } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { Wrench, Plus, Trash2, Pencil, Check, X, Activity, ChevronDown, ChevronUp } from 'lucide-react'
import { ZustandBadge } from './AkkuDetail'
import { DatumFeld } from './DatumFelder'

// Wartungsverwaltung einer Drohne: Status je Punkt des Wartungsplans (je Drohnenmodell), Wartungsbuch und
// HMS-Meldungen aus der DJI Cloud API. Berechnung in der Admin-API (drohnen_wartung.bewertung).
// Herstellerwartungen können in Eigenregie oder durch den Hersteller erledigt werden.

interface PlanPunkt {
  id: number
  bezeichnung: string
  quelle: 'HERSTELLER' | 'EIGEN'
  quelle_label: string
  intervall_stunden: number | null
  intervall_monate: number | null
  intervall_fluege: number | null
  beschreibung: string | null
  aktiv: boolean
  sortierung: number
}

interface PlanStatus extends PlanPunkt {
  status: 'ok' | 'bald' | 'faellig'
  letzte_wartung: string | null
  letzte_durchfuehrung: string | null
  seit_stunden: number
  seit_monate: number | null
  seit_fluege: number
  naechste_am: string | null
  naechste_bei_stunden: number | null
  naechste_bei_fluegen: number | null
}

interface Wartung {
  id: number
  plan_id: number | null
  bezeichnung: string
  datum: string
  durchfuehrung: string
  durchfuehrung_label: string
  quelle: string | null
  flugstunden: number | null
  fluege: number | null
  firmware: string | null
  notiz: string | null
  erfasst_von: string | null
}

interface Hms {
  id: number
  code: string
  stufe: number | null
  stufe_label: string
  in_der_luft: boolean | null
  erstmals: string
  zuletzt: string
  behoben_am: string | null
}

interface WartungDaten {
  verwaltet: boolean
  zustand: 'gut' | 'wartung' | 'pruefen' | null
  zustand_gruende: string[]
  plan_status: PlanStatus[]
  flugstunden: number
  flugstunden_quelle: string
  fluege: number
  firmware_version: string | null
  inbetriebnahme: string | null
  wartungen: Wartung[]
  hms: Hms[]
}

const datum = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')
const zeit = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const h = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`)

function intervallText(p: PlanPunkt) {
  const teile = [
    p.intervall_stunden ? `${p.intervall_stunden.toLocaleString('de-DE')} h` : null,
    p.intervall_monate ? `${p.intervall_monate} Monate` : null,
    p.intervall_fluege ? `${p.intervall_fluege} Flüge` : null,
  ].filter(Boolean)
  return teile.join(' / ') + (teile.length > 1 ? ' – was zuerst eintritt' : '')
}

function QuelleBadge({ quelle }: { quelle: string | null }) {
  if (!quelle) return null
  return quelle === 'HERSTELLER'
    ? <span className="rounded border border-primary-500/40 bg-primary-500/10 px-1.5 py-0.5 text-[10px] font-medium text-primary-200">Hersteller</span>
    : <span className="rounded border border-dark-border bg-dark-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-dark-text-muted">Eigen</span>
}

function StatusChip({ status }: { status: PlanStatus['status'] }) {
  if (status === 'faellig') return <Badge variant="warning">⚠ fällig</Badge>
  if (status === 'bald') return <Badge variant="info">bald fällig</Badge>
  return <Badge variant="success">✓ ok</Badge>
}

function Abschnitt({ titel, icon, rechts, children }: { titel: string; icon: ReactNode; rechts?: ReactNode; children: ReactNode }) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 font-medium text-dark-text">{icon}{titel}</h4>
        {rechts}
      </div>
      {children}
    </Card>
  )
}

const LEER_PUNKT = { bezeichnung: '', quelle: 'EIGEN', intervall_stunden: '', intervall_monate: '', intervall_fluege: '', beschreibung: '', aktiv: true }
const heute = () => new Date().toISOString().slice(0, 10)

export function DrohneWartung({ drohneId, referenzId, modellName, onGeaendert }: {
  drohneId: number
  referenzId: number | null
  modellName: string | null
  /** Nach Änderungen: Drohnenliste neu laden (Zustand-Spalte) */
  onGeaendert: () => void
}) {
  const { bestaetigen, dialog } = useBestaetigung()
  const [daten, setDaten] = useState<WartungDaten | null>(null)
  const [plan, setPlan] = useState<PlanPunkt[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [neu, setNeu] = useState({ plan_id: '', bezeichnung: '', datum: heute(), durchfuehrung: 'EIGENREGIE', flugstunden: '', notiz: '' })
  const [planOffen, setPlanOffen] = useState(false)
  const [punktForm, setPunktForm] = useState<Record<string, any>>({ ...LEER_PUNKT })
  const [bearbeiteId, setBearbeiteId] = useState<number | null>(null)

  const laden = useCallback(() => {
    api.get(`/drohnen/${drohneId}/wartung`).then(r => setDaten(r.data as WartungDaten)).catch(() => setDaten(null))
    if (referenzId) api.get(`/drohnen-modelle/${referenzId}/wartungsplan`).then(r => setPlan((r.data as any).items ?? [])).catch(() => setPlan([]))
  }, [drohneId, referenzId])
  useEffect(() => { laden() }, [laden])

  async function aktion(fn: () => Promise<unknown>, danach?: () => void) {
    setSpeichert(true); setFehler(null)
    try {
      await fn()
      danach?.()
      laden(); onGeaendert()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally { setSpeichert(false) }
  }

  if (!daten) return null
  if (!daten.verwaltet) {
    return (
      <Abschnitt titel="Wartung" icon={<Wrench className="h-4 w-4 text-primary-400" />}>
        <p className="text-sm text-dark-text-muted">Private Drohnen von Piloten sind nicht Teil der Wartungsverwaltung der Staffel.</p>
      </Abschnitt>
    )
  }

  const gewaehlterPunkt = plan.find(p => String(p.id) === neu.plan_id)

  return (
    <div className="space-y-4">
      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-2 text-sm text-red-200">{fehler}</div>}
      {dialog}

      {/* Überblick */}
      <Abschnitt titel="Wartung" icon={<Wrench className="h-4 w-4 text-primary-400" />}
        rechts={daten.zustand && <ZustandBadge zustand={daten.zustand} gruende={daten.zustand_gruende} />}>
        <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          <div className="flex justify-between"><dt className="text-dark-text-muted">Flugstunden</dt>
            <dd>{h(daten.flugstunden)} <span className="text-xs text-dark-text-subtle">({daten.flugstunden_quelle})</span></dd></div>
          <div className="flex justify-between"><dt className="text-dark-text-muted">Flüge (Flugbuch)</dt><dd>{daten.fluege}</dd></div>
          <div className="flex justify-between"><dt className="text-dark-text-muted">Firmware (DJI)</dt><dd>{daten.firmware_version || '—'}</dd></div>
          <div className="flex justify-between"><dt className="text-dark-text-muted">Inbetriebnahme</dt><dd>{datum(daten.inbetriebnahme)}</dd></div>
        </dl>
        {daten.zustand_gruende.length > 0 && (
          <ul className="mt-3 space-y-0.5 border-t border-dark-border pt-2 text-xs text-dark-text-muted">
            {daten.zustand_gruende.map(g => <li key={g}>• {g}</li>)}
          </ul>
        )}
        {!daten.inbetriebnahme && (
          <p className="mt-2 text-xs text-dark-text-subtle">Tipp: Inbetriebnahme unter „Bearbeiten“ eintragen – sonst zählen Monats-Intervalle erst ab der ersten Wartung.</p>
        )}
      </Abschnitt>

      {/* Wartungsplan mit Status */}
      <Abschnitt titel={`Wartungsplan${modellName ? ` – ${modellName}` : ''}`} icon={<Wrench className="h-4 w-4 text-primary-400" />}
        rechts={referenzId && (
          <Button size="sm" variant="secondary" onClick={() => setPlanOffen(o => !o)} aria-expanded={planOffen}>
            {planOffen ? <ChevronUp className="mr-1 h-4 w-4" /> : <Pencil className="mr-1 h-4 w-4" />}Plan bearbeiten
          </Button>
        )}>
        {!referenzId ? (
          <p className="text-sm text-dark-text-muted">Der Drohne ist kein Modell zugeordnet. Der Wartungsplan gilt je Modell – Modell unter „Bearbeiten“ wählen.</p>
        ) : daten.plan_status.length === 0 ? (
          <p className="text-sm text-dark-text-muted">Für dieses Modell ist noch kein Wartungsplan angelegt. Über „Plan bearbeiten“ Wartungspunkte mit Intervallen anlegen.</p>
        ) : (
          <>
          <ul className="space-y-3 md:hidden">
            {daten.plan_status.map(p => (
              <li key={p.id} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
                <div className="flex flex-wrap items-center gap-2 text-base font-semibold">{p.bezeichnung}<QuelleBadge quelle={p.quelle} /></div>
                <div className="mt-2"><StatusChip status={p.status} /></div>
                <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                  <dt className="text-dark-text-subtle">Intervall</dt><dd>{intervallText(p)}</dd>
                  <dt className="text-dark-text-subtle">Letzte Wartung</dt><dd>{p.letzte_wartung ? <>{datum(p.letzte_wartung)}<div className="text-xs text-dark-text-subtle">{p.letzte_durchfuehrung}</div></> : 'noch nie'}</dd>
                  <dt className="text-dark-text-subtle">Seitdem</dt><dd>{[p.intervall_stunden ? h(p.seit_stunden) : null, p.intervall_monate && p.seit_monate != null ? `${p.seit_monate} Mon.` : null, p.intervall_fluege ? `${p.seit_fluege} Flüge` : null].filter(Boolean).join(' · ') || '—'}</dd>
                  <dt className="text-dark-text-subtle">Nächste fällig</dt><dd>{[p.naechste_am ? datum(p.naechste_am) : null, p.naechste_bei_stunden != null ? `bei ${h(p.naechste_bei_stunden)}` : null, p.naechste_bei_fluegen != null ? `bei ${p.naechste_bei_fluegen} Flügen` : null].filter(Boolean).map(t => <div key={t}>{t}</div>)}</dd>
                </dl>
                <div className="mt-3 flex justify-end border-t border-dark-border pt-3">
                  <Button variant="secondary" className="min-h-12" onClick={() => setNeu({ ...neu, plan_id: String(p.id), bezeichnung: '', datum: heute() })}>
                    <Check className="mr-1 h-4 w-4" />Erledigt
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-dark-text-muted">
                <tr>{['Wartungspunkt', 'Intervall', 'Letzte Wartung', 'Seitdem', 'Nächste fällig', 'Status', ''].map((t, i) =>
                  <th key={i} className="whitespace-nowrap px-2 py-1.5 font-medium">{t}</th>)}</tr>
              </thead>
              <tbody>
                {daten.plan_status.map(p => (
                  <tr key={p.id} className="border-t border-dark-border align-top">
                    <td className="px-2 py-2"><div className="flex flex-wrap items-center gap-1.5">{p.bezeichnung}<QuelleBadge quelle={p.quelle} /></div></td>
                    <td className="px-2 py-2 text-xs text-dark-text-muted">{intervallText(p)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-xs">{p.letzte_wartung ? <>{datum(p.letzte_wartung)}<div className="text-dark-text-subtle">{p.letzte_durchfuehrung}</div></> : 'noch nie'}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-xs tabular-nums">
                      {[p.intervall_stunden ? h(p.seit_stunden) : null, p.intervall_monate && p.seit_monate != null ? `${p.seit_monate} Mon.` : null,
                        p.intervall_fluege ? `${p.seit_fluege} Flüge` : null].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 text-xs tabular-nums">
                      {[p.naechste_am ? datum(p.naechste_am) : null, p.naechste_bei_stunden != null ? `bei ${h(p.naechste_bei_stunden)}` : null,
                        p.naechste_bei_fluegen != null ? `bei ${p.naechste_bei_fluegen} Flügen` : null].filter(Boolean).map(t => <div key={t}>{t}</div>)}
                    </td>
                    <td className="px-2 py-2"><StatusChip status={p.status} /></td>
                    <td className="px-2 py-2 text-right">
                      <Button size="sm" variant="secondary" onClick={() => setNeu({ ...neu, plan_id: String(p.id), bezeichnung: '', datum: heute() })}>
                        <Check className="mr-1 h-4 w-4" />Erledigt
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Plan bearbeiten (gilt für alle Drohnen des Modells) */}
        {planOffen && referenzId && (
          <div className="mt-4 space-y-3 border-t border-dark-border pt-4">
            <p className="text-xs text-dark-text-subtle">Der Plan gilt für alle Drohnen dieses Modells. Intervalle: fällig ist, was zuerst eintritt. Mindestens ein Intervall angeben.</p>
            {plan.map(p => (
              <div key={p.id} className={`flex flex-wrap items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-sm ${p.aktiv ? '' : 'opacity-60'}`}>
                <span className="font-medium">{p.bezeichnung}</span><QuelleBadge quelle={p.quelle} />
                <span className="text-xs text-dark-text-muted">{intervallText(p)}{p.aktiv ? '' : ' · inaktiv'}</span>
                <span className="ml-auto flex gap-3 md:gap-1">
                  <button type="button" title="Bearbeiten" aria-label={`${p.bezeichnung} bearbeiten`} className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-blue-400 hover:bg-blue-400/10 md:h-9 md:w-9"
                    onClick={() => { setBearbeiteId(p.id); setPunktForm({ ...p, intervall_stunden: p.intervall_stunden ?? '', intervall_monate: p.intervall_monate ?? '', intervall_fluege: p.intervall_fluege ?? '', beschreibung: p.beschreibung ?? '' }) }}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" title="Löschen" aria-label={`${p.bezeichnung} löschen`} className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10 md:h-9 md:w-9"
                    onClick={async () => { if (await bestaetigen({ titel: 'Wartungspunkt löschen?', text: 'Wartungspunkt', objekt: p.bezeichnung, hinweis: 'Der Punkt entfällt im Wartungsplan aller Drohnen dieses Modells; die Fälligkeitsanzeige verschwindet. Bereits eingetragene Wartungen im Wartungsbuch bleiben erhalten, verlieren aber die Zuordnung zum Punkt.' })) aktion(() => api.delete(`/drohnen-wartungsplan/${p.id}`)) }}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </span>
              </div>
            ))}
            <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary/40 p-3">
              <div className="mb-2 text-sm font-medium">{bearbeiteId ? 'Wartungspunkt ändern' : 'Neuer Wartungspunkt'}</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="label">Bezeichnung</label>
                  <Input value={punktForm.bezeichnung} onChange={e => setPunktForm({ ...punktForm, bezeichnung: e.target.value })} placeholder="z. B. Herstellerwartung Basic, Propeller tauschen" />
                </div>
                <div>
                  <label className="label">Quelle</label>
                  <Select value={punktForm.quelle} onChange={e => setPunktForm({ ...punktForm, quelle: e.target.value })}
                    options={[{ value: 'HERSTELLER', label: 'Herstellervorgabe' }, { value: 'EIGEN', label: 'Eigene Festlegung (Betriebshandbuch)' }]} />
                </div>
                <div className="grid grid-cols-3 gap-2 sm:col-span-2">
                  <div><label className="label">alle … Flugstunden</label><Input type="number" min={0} step="0.5" value={punktForm.intervall_stunden} onChange={e => setPunktForm({ ...punktForm, intervall_stunden: e.target.value })} /></div>
                  <div><label className="label">alle … Monate</label><Input type="number" min={1} value={punktForm.intervall_monate} onChange={e => setPunktForm({ ...punktForm, intervall_monate: e.target.value })} /></div>
                  <div><label className="label">alle … Flüge</label><Input type="number" min={1} value={punktForm.intervall_fluege} onChange={e => setPunktForm({ ...punktForm, intervall_fluege: e.target.value })} /></div>
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Beschreibung / Umfang</label>
                  <Input value={punktForm.beschreibung} onChange={e => setPunktForm({ ...punktForm, beschreibung: e.target.value })} placeholder="z. B. laut Wartungshandbuch Kap. 3" />
                </div>
                <label className="flex items-center gap-2 text-sm text-dark-text-muted">
                  <input type="checkbox" checked={!!punktForm.aktiv} onChange={e => setPunktForm({ ...punktForm, aktiv: e.target.checked })} className="h-4 w-4 accent-primary-500" />
                  aktiv (wird bewertet)
                </label>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                {bearbeiteId && <Button size="sm" variant="secondary" onClick={() => { setBearbeiteId(null); setPunktForm({ ...LEER_PUNKT }) }}><X className="mr-1 h-4 w-4" />Abbrechen</Button>}
                <Button size="sm" disabled={speichert || !punktForm.bezeichnung} onClick={() => aktion(
                  () => bearbeiteId ? api.put(`/drohnen-wartungsplan/${bearbeiteId}`, punktForm) : api.post(`/drohnen-modelle/${referenzId}/wartungsplan`, punktForm),
                  () => { setBearbeiteId(null); setPunktForm({ ...LEER_PUNKT }) })}>
                  <Check className="mr-1 h-4 w-4" />{bearbeiteId ? 'Speichern' : 'Hinzufügen'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Abschnitt>

      {/* Wartung eintragen + Wartungsbuch */}
      <Abschnitt titel="Wartungsbuch" icon={<Wrench className="h-4 w-4 text-primary-400" />}>
        <div className="mb-4 rounded-lg border border-dark-border bg-dark-bg-tertiary/40 p-3">
          <div className="mb-2 text-sm font-medium">Wartung eintragen</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Wartungspunkt</label>
              <Select value={neu.plan_id} onChange={e => setNeu({ ...neu, plan_id: e.target.value })}
                options={[...daten.plan_status.map(p => ({ value: String(p.id), label: `${p.bezeichnung}${p.quelle === 'HERSTELLER' ? ' (Herstellervorgabe)' : ''}` })),
                  { value: '', label: 'Sonstige Wartung / Reparatur (frei)' }]} />
            </div>
            {!neu.plan_id && (
              <div>
                <label className="label">Bezeichnung</label>
                <Input value={neu.bezeichnung} onChange={e => setNeu({ ...neu, bezeichnung: e.target.value })} placeholder="z. B. Motor 3 getauscht" />
              </div>
            )}
            <div>
              <label className="label">Datum</label>
              <DatumFeld  value={neu.datum} onChange={v => setNeu({ ...neu, datum: v })} />
            </div>
            <div className="sm:col-span-2">
              <span className="label">Durchführung</span>
              <div className="flex flex-wrap gap-2" role="radiogroup">
                {[['EIGENREGIE', 'In Eigenregie'], ['HERSTELLER', 'Durch den Hersteller']].map(([wert, label]) => (
                  <button key={wert} type="button" role="radio" aria-checked={neu.durchfuehrung === wert} onClick={() => setNeu({ ...neu, durchfuehrung: wert })}
                    className={`min-h-[40px] rounded-lg border px-3 text-sm transition-colors ${neu.durchfuehrung === wert ? 'border-primary-500 bg-primary-500/15 text-dark-text' : 'border-dark-border text-dark-text-muted hover:border-primary-500/60'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {gewaehlterPunkt?.quelle === 'HERSTELLER' && neu.durchfuehrung === 'EIGENREGIE' && (
                <p className="mt-1 text-xs text-dark-text-subtle">Herstellerwartung in Eigenregie – im Wartungsbuch und Export als „in Eigenregie“ vermerkt.</p>
              )}
            </div>
            <div>
              <label className="label">Flugstunden bei Wartung</label>
              <Input type="number" min={0} step="0.1" value={neu.flugstunden} onChange={e => setNeu({ ...neu, flugstunden: e.target.value })} placeholder={`aktuell ${daten.flugstunden.toLocaleString('de-DE')}`} />
              <p className="mt-1 text-xs text-dark-text-subtle">Leer = aktueller Stand. Nur für nachgetragene Wartungen ändern.</p>
            </div>
            <div>
              <label className="label">Notiz</label>
              <Input value={neu.notiz} onChange={e => setNeu({ ...neu, notiz: e.target.value })} placeholder="z. B. ohne Befund, Teile getauscht" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button disabled={speichert || !neu.datum || (!neu.plan_id && !neu.bezeichnung)} onClick={() => aktion(
              () => api.post(`/drohnen/${drohneId}/wartungen`, { ...neu, plan_id: neu.plan_id ? Number(neu.plan_id) : null }),
              () => setNeu({ plan_id: '', bezeichnung: '', datum: heute(), durchfuehrung: 'EIGENREGIE', flugstunden: '', notiz: '' }))}>
              <Plus className="mr-1 h-4 w-4" />Eintragen
            </Button>
          </div>
        </div>

        <ul className="space-y-3 md:hidden">
          {daten.wartungen.length === 0 && <li className="py-6 text-center text-sm text-dark-text-subtle">Noch keine Wartungen erfasst.</li>}
          {daten.wartungen.map(w => (
            <li key={w.id} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
              <div className="text-base font-semibold">{datum(w.datum)}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2">{w.bezeichnung}<QuelleBadge quelle={w.quelle} /></div>
              <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-dark-text-subtle">Durchführung</dt><dd>{w.durchfuehrung_label}</dd>
                <dt className="text-dark-text-subtle">Stand</dt><dd>{h(w.flugstunden)} · {w.fluege ?? '—'} Flüge{w.firmware ? ` · FW ${w.firmware}` : ''}</dd>
                <dt className="text-dark-text-subtle">Notiz</dt><dd>{w.notiz || '—'}</dd>
                <dt className="text-dark-text-subtle">Erfasst von</dt><dd>{w.erfasst_von || '—'}</dd>
              </dl>
              <div className="mt-3 flex justify-end border-t border-dark-border pt-3">
                <button type="button" aria-label={`Wartungseintrag vom ${datum(w.datum)} löschen`} className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-red-500 hover:bg-red-400/10"
                  onClick={async () => { if (await bestaetigen({ titel: 'Wartungseintrag löschen?', text: 'Wartungseintrag', objekt: `${w.bezeichnung} vom ${datum(w.datum)}`, hinweis: 'Der Eintrag wird aus dem Wartungsbuch entfernt. „Letzte Wartung“, Fälligkeiten und der Wartungszustand der Drohne werden neu berechnet.' })) aktion(() => api.delete(`/drohnen/${drohneId}/wartungen/${w.id}`)) }}>
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-dark-text-muted">
              <tr>{['Datum', 'Wartung', 'Durchführung', 'Stand', 'Notiz', 'Erfasst von', ''].map((t, i) => <th key={i} className="whitespace-nowrap px-2 py-1.5 font-medium">{t}</th>)}</tr>
            </thead>
            <tbody>
              {daten.wartungen.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-dark-text-subtle">Noch keine Wartungen erfasst.</td></tr>}
              {daten.wartungen.map(w => (
                <tr key={w.id} className="border-t border-dark-border align-top">
                  <td className="whitespace-nowrap px-2 py-2">{datum(w.datum)}</td>
                  <td className="px-2 py-2"><div className="flex flex-wrap items-center gap-1.5">{w.bezeichnung}<QuelleBadge quelle={w.quelle} /></div></td>
                  <td className="whitespace-nowrap px-2 py-2 text-dark-text-muted">{w.durchfuehrung_label}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs tabular-nums text-dark-text-muted">{h(w.flugstunden)} · {w.fluege ?? '—'} Flüge{w.firmware ? <div>FW {w.firmware}</div> : null}</td>
                  <td className="px-2 py-2 text-dark-text-muted">{w.notiz || '—'}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-dark-text-muted">{w.erfasst_von || '—'}</td>
                  <td className="px-2 py-2 text-right">
                    <button type="button" title="Eintrag löschen" aria-label="Wartungseintrag löschen" className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10 md:h-9 md:w-9"
                      onClick={async () => { if (await bestaetigen({ titel: 'Wartungseintrag löschen?', text: 'Wartungseintrag', objekt: `${w.bezeichnung} vom ${datum(w.datum)}`, hinweis: 'Der Eintrag wird aus dem Wartungsbuch entfernt. „Letzte Wartung“, Fälligkeiten und der Wartungszustand der Drohne werden neu berechnet.' })) aktion(() => api.delete(`/drohnen/${drohneId}/wartungen/${w.id}`)) }}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Abschnitt>

      {/* HMS */}
      <Abschnitt titel="Meldungen der Drohne (HMS)" icon={<Activity className="h-4 w-4 text-primary-400" />}>
        {daten.hms.length === 0 ? (
          <p className="text-sm text-dark-text-muted">Keine offenen oder kürzlich behobenen HMS-Meldungen. Sie werden über DJI Pilot 2 empfangen, sobald die Drohne verbunden ist.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {daten.hms.map(m => (
              <li key={m.id} className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${m.behoben_am ? 'border-dark-border text-dark-text-muted' : m.stufe === 2 ? 'border-red-500/40 bg-red-500/10' : m.stufe === 1 ? 'border-yellow-500/40 bg-yellow-500/10' : 'border-dark-border'}`}>
                <Badge variant={m.behoben_am ? 'info' : m.stufe === 2 ? 'danger' : m.stufe === 1 ? 'warning' : 'info'}>{m.behoben_am ? 'behoben' : m.stufe_label}</Badge>
                <span className="font-mono text-xs">{m.code}</span>
                <span className="text-xs text-dark-text-subtle">seit {zeit(m.erstmals)}{m.behoben_am ? ` · behoben ${zeit(m.behoben_am)}` : ''}{m.in_der_luft ? ' · im Flug' : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>
    </div>
  )
}
